import { useEffect, useState } from 'react';

import { IDLE_TIMEOUT_IN_MS } from '@/lib/const';

/** Excludes mousemove: cursor drift while reading is not interaction. */
const INTERACTION_EVENTS = [
  'pointerdown',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
] as const;

/** Ignore repeat resets inside this window so a scroll burst costs one timer. */
const RESET_THROTTLE_IN_MS = 200;

/**
 * True once the user has gone `timeout` ms without interacting, and latching:
 * it never flips back, so gated UI cannot vanish as the user reaches for it.
 *
 * False on the server and first client render, doubling as a hydration guard.
 *
 * `enabled: false` arms no timer and registers no listeners.
 * `resetKey` restarts the countdown when it changes — callers pass the path.
 */
export const useIdle = (
  timeout: number = IDLE_TIMEOUT_IN_MS,
  enabled = true,
  resetKey?: string,
) => {
  const [hasBeenIdle, setHasBeenIdle] = useState(false);
  const [latchedFor, setLatchedFor] = useState(resetKey);

  // Cleared during render, not in an effect, so a new page never paints with
  // the previous page's idle state.
  if (latchedFor !== resetKey) {
    setLatchedFor(resetKey);
    setHasBeenIdle(false);
  }

  useEffect(() => {
    if (hasBeenIdle || !enabled) return;

    let timer: ReturnType<typeof setTimeout>;
    let lastReset = 0;

    const start = () => {
      timer = setTimeout(() => setHasBeenIdle(true), timeout);
    };

    const reset = () => {
      const now = Date.now();
      if (now - lastReset < RESET_THROTTLE_IN_MS) return;
      lastReset = now;
      clearTimeout(timer);
      start();
    };

    start();
    INTERACTION_EVENTS.forEach((eventType) =>
      window.addEventListener(eventType, reset, { passive: true }),
    );

    return () => {
      clearTimeout(timer);
      INTERACTION_EVENTS.forEach((eventType) =>
        window.removeEventListener(eventType, reset),
      );
    };
  }, [timeout, hasBeenIdle, enabled, resetKey]);

  return hasBeenIdle;
};
